<template>
  <div id="container">
    <!-- Change to @click to disable double click but have no lag -->
    <vl-map
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      @singleclick="clickCoordinate = $event.coordinate"
    >
      <vl-view
        :projection="projection"
        :zoom.sync="zoom"
        :center.sync="center"
        :rotation.sync="rotation"
      ></vl-view>

      <vl-layer-image id="xkcd">
        <vl-source-image-static
          :url="imgUrl"
          :size="imgSize"
          :extent="imgExtent"
          :projection="projection"
        ></vl-source-image-static>
      </vl-layer-image>

      <!-- All locations pins -->
      <div v-if="showAllLocations">
        <div v-for="loc in sortedLocs" :key="loc.y">
          <vl-overlay
            class
            :position="[imgSize[0] * loc.x, imgSize[1] * loc.y]"
            positioning="bottom-center"
            :hidden="!showAllLocations"
            :stopEvent="false"
          >
            <div v-if="loc.name[0] == '@'" class="user-avatar">
              <img
                class="avatar-img"
                v-if="$store.getters.userToImage[loc.name.substring(1)]"
                v-bind:src="$store.getters.userToImage[loc.name.substring(1)]"
              />
              <img class="avatar-img" v-else src="../../public/user_72.png" />
            </div>
            <button
              :class="
                'button is-info location-label ' +
                  (loc.name[0] == '@'
                    ? 'map-show-all-label'
                    : 'map-show-all-label-nonperson')
              "
              aria-label="Add this location"
            >
              {{ loc.name }}
            </button>
            <div>
              <b-icon
                icon="map-marker"
                pack="fas"
                size="is-small"
                :style="
                  loc.name[0] == '@' ? 'color: #8a51a2;' : 'color: #55a541'
                "
              ></b-icon>
            </div>
          </vl-overlay>
        </div>
      </div>

      <!-- Selected location pin -->
      <p v-if="shouldDisplayLocation()">
        <vl-overlay
          class="map-selected-existing"
          :position="[
            this.imgSize[0] * this.$store.getters.selectedLocation.x,
            this.imgSize[1] * this.$store.getters.selectedLocation.y
          ]"
          positioning="bottom-center"
          :stopEvent="false"
        >
          <div>
            <div
              v-if="this.$store.getters.selectedLocation.name[0] == '@'"
              class="user-avatar map-selected-existing-user-avatar"
            >
              <img
                class="avatar-img"
                v-if="
                  $store.getters.userToImage[
                    this.$store.getters.selectedLocation.name.substring(1)
                  ]
                "
                v-bind:src="
                  $store.getters.userToImage[
                    this.$store.getters.selectedLocation.name.substring(1)
                  ]
                "
              />
              <img class="avatar-img" v-else src="../../public/user_72.png" />
            </div>
            <button
              class="button is-info location-label map-selected-existing-label"
              aria-label="Location name"
            >
              {{ this.$store.getters.selectedLocation.name }}
            </button>
            <div>
              <b-icon icon="map-marker" pack="fas" size="is-small"></b-icon>
            </div>
          </div>
        </vl-overlay>
      </p>

      <!-- Add new location box -->
      <div v-if="clickCoordinate !== undefined">
        <vl-overlay
          class="map-selected-nonexisting"
          :position="[
            clickCoordinate[0],
            clickCoordinate[1] + 200 / (2 ** zoom || 1)
          ]"
          positioning="bottom-center"
        >
          <div class="box media">
            <span class="add-location-field">
              <b-field>
                <b-autocomplete
                  v-model="newLocationName"
                  :data="filteredAutocompleteDataArray"
                  placeholder="Enter location name"
                  clearable
                  open-on-focus
                  keep-first
                  @select="option => (selectedAutocompleteOption = option)"
                  class="unset-width"
                >
                </b-autocomplete>
                <p class="control">
                  <button
                    class="button is-primary"
                    @click="addNewLocation"
                    aria-label="Save location"
                  >
                    Save
                  </button>
                </p>
              </b-field>
              <div id="location-name-hint">
                Hint: Start with @ when adding a person.
              </div>
            </span>
            <span class="close-button">
              <button
                class="delete is-small"
                type="button"
                :aria-label="ariaCloseLabel"
                @click="clickCoordinate = undefined"
              />
            </span>
          </div>
        </vl-overlay>
        <vl-overlay
          class="map-selected-nonexisting"
          :position="[clickCoordinate[0], clickCoordinate[1]]"
          positioning="bottom-center"
        >
          <div>
            <div class="add-pin-icon">
              <b-icon icon="map-pin" pack="fas" size="is-medium"></b-icon>
            </div>
          </div>
        </vl-overlay>
      </div>
    </vl-map>

    <!-- Building and floor name box -->
    <div id="box" class="is-hidden-mobile">
      <div class="columns is-1 is-variable" style>
        <div class="column">
          <div>Building:</div>
          <div>Floor:</div>
          <div>Show all:</div>
        </div>
        <div class="column" style="white-space: nowrap;" align="left">
          <div>
            <strong>{{ $store.getters.selectedBuilding.name }}</strong>
          </div>
          <div>
            <strong>{{ $store.getters.selectedFloor.name }}</strong>
          </div>
          <div class="field">
            <b-switch size="is-small" v-model="showAllLocations"></b-switch>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createProj, addProj } from "vuelayers/lib/ol-ext";
import { NotificationProgrammatic as Notification } from "buefy/dist/components/notification";
import { Map, ImageLayer, ImageStaticSource, Overlay } from "vuelayers";
import { addLocation } from "../api-requests.js";

// Map views always need a projection.  Here we just want to map image
// coordinates directly to map coordinates, so we create a projection that uses
// the image extent in pixels.
let size = [1000, 1000];
let extent = [0, 0, 1000, 1000];
// create custom projection for image
// NOTE: VueLayers.olExt available only in UMD build
// in ES build it should be imported explicitly from 'vuelayers/lib/ol-ext'
let customProj = createProj({
  code: "xkcd-image",
  units: "pixels",
  extent
});
// add it to the list of known projections
addProj(customProj);

export default {
  name: "OfficeMap",
  components: {},
  created: async function() {
    window.App.use(Map);
    window.App.use(ImageLayer);
    window.App.use(ImageStaticSource);
    window.App.use(Overlay);
    setTimeout(() => {
      this.displayFloorViewInfoPanel();
    }, 1500);
  },
  data() {
    const prefixedUserDisplayNames = [];
    this.$store.getters.userDisplayNames.forEach(userDisplayName => {
      if (userDisplayName.length > 0) {
        prefixedUserDisplayNames.push("@" + userDisplayName);
      }
    });

    return {
      zoom: 0.4,
      maxZoom: 8,
      rotation: 0,
      checked: false,
      clickCoordinate: undefined,
      projection: customProj.getCode(),
      imgSize: size,
      imgExtent: extent,
      center: [500, 500],
      // If it changes, we force redraw to put pin+label in the correct place.
      lastChosenLocation: undefined,
      imgUrl: "",
      showAllLocations: false,
      prefixedUserDisplayNames: prefixedUserDisplayNames,
      selectedAutocompleteOption: null,
      newLocationName: ""
    };
  },
  computed: {
    foo: function() {
      // console.log("Calling computation of image data")
      this.center = [500, 500];
      this.zoom = 1.3;
      this.imgUrl = this.$store.getters.selectedFloor.hi_res_url;
      // console.log("Image url:", this.imgUrl);
      return this.$store.getters.selectedFloor.hi_res_url;
    },
    clickCoordinateNormalized: function() {
      if (this.clickCoordinate === undefined) return [0, 0];
      // console.log("Clicked on map: " + this.clickCoordinate);
      const coords = [
        this.clickCoordinate[0] / 1000,
        this.clickCoordinate[1] / 1000
      ];
      this.$store.commit("SET_SELECTED_LOCATION_ID", undefined);
      // console.log(coords);
      return coords;
    },
    floorSize: function() {
      // This is obviously not the image size, as it should be.
      // Because of this, coordinates are not between 0 and 1.
      return { size_x: 1000, size_y: 1000 };
    },
    filteredAutocompleteDataArray() {
      return this.prefixedUserDisplayNames.filter(option => {
        return (
          option
            .toString()
            .toLowerCase()
            .indexOf(this.newLocationName.toLowerCase()) >= 0
        );
      });
    },
    sortedLocs() {
      const copiedLocs = [...this.$store.getters.selectedFloor.locations];
      return copiedLocs.sort((a, b) => (a.y > b.y ? 1 : -1));
    }
  },
  watch: {
    foo: function(newFloor) {
      // console.log("new currentFloor")
    },
    clickCoordinateNormalized: function(newCoordsNormalized) {
      // console.log("new clicked normalized: " + newCoordsNormalized);
    },
    "$store.state.selectedLocationId": function(newSelectedLocationId) {
      console.log("a new loc has been selected");
      if (newSelectedLocationId !== undefined) {
        this.center = [
          this.imgSize[0] * this.$store.getters.selectedLocation.x,
          this.imgSize[1] * this.$store.getters.selectedLocation.y
        ];
        this.zoom = 1.3;
        this.clickCoordinate = undefined;
      }
    },
    showAllLocations: function(newShowAllLocations) {
      // console.log("showAllLocations: ", newShowAllLocations);
      // We need to force redraw or otherwise the map will show
      // pins on incorrect coordinates.
      // this.center = [this.center[0] + 0.000000001, this.center[1]];
      if (newShowAllLocations === true) {
        // Recenter the map and set the default zoom to show the full map.
        this.center = [500, 500];
        this.zoom = 1.3;
      }
    }
  },
  methods: {
    addNewLocation: async function() {
      const coords = this.clickCoordinateNormalized;
      const floor_id = this.$store.getters.selectedFloor.id;
      await addLocation(this.newLocationName, coords[0], coords[1], floor_id);
      this.clickCoordinate = undefined;
      const addedLocation = this.$store.getters.selectedFloor.locations.find(
        loc => loc.name == this.newLocationName
      );
      this.$store.commit("SET_SELECTED_LOCATION_ID", addedLocation.id);
      this.newLocationName = "";
    },
    shouldDisplayLocation: function() {
      // Bad performance on lots of floors!
      const selected = this.$store.getters.selectedLocation;
      if (selected === undefined) {
        return false;
      }
      const found = this.$store.getters.selectedFloor.locations.find(
        loc => loc == selected
      );
      const should = found !== undefined;
      if (should && this.lastChosenLocation !== selected) {
        this.center = [this.center[0] + 0.01, this.center[1]];
        this.lastChosenLocation = selected;
        // console.log("Redrawing");
        // console.log("lastChosenLocation: ", this.lastChosenLocation);
      }
      return found !== undefined;
    },
    displayFloorViewInfoPanel: function() {
      if (this.$store.getters.shouldDisplayFloorViewInfoPanel) {
        Notification.open({
          message: `Welcome to the floor view!<br>
          Click on the map to add a new location.<br>`,
          // message: `Welcome to the floor view!<br><br>
          // &bull; Click on the map to add a new location.<br>
          // &bull; Select a location from the left panel to see it on the map.<br>
          // &bull; Toggle "Show all" in the top-right corner to see all pins at once.<br>
          // &bull; Use the mouse scroll to zoom in and out.`,
          indefinite: true,
          position: "is-bottom-right",
          type: "is-info",
          hasIcon: true
        });
        this.$store.commit("setShouldDisplayFloorViewInfoPanelToFalse");
        // Event emited when closing can be handled in Buefy version >= v.8.9.0.
        // notif.$on("close", () => { console.log("Closed the notification."); });
      }
    }
  }
};
</script>

<style scoped>
#container {
  background: #fbf8f8;
  position: relative;
}

#box {
  position: absolute;
  top: 10px;
  right: 10px;
  background: white;
  padding: 8px;
  border-radius: 6px;
  text-align: right;
  color: gray;
  border-color: lightgrey;
  border-style: solid;
  border-width: 1px;
}

#map {
  position: relative;
  min-height: 100%;
}
#infobox-text {
  background: white;
  padding: 4px;
  border-radius: 10px;
  text-align: right;
  color: black;
  border-color: black;
  border-style: solid;
  border-width: 1px;
}

#infobox-location {
  position: absolute;
  top: 10px;
  right: 45%;
}

#infobox {
}

.map-selected-existing {
  color: #f40076;
}
.map-selected-nonexisting {
  color: #209cee;
}
#map-selected {
  color: #f40076;
}
.map-selected-existing-label {
  background-color: #f40076;
  border: 1px solid white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 100px;
}
.map-selected-existing-label:hover {
  background-color: #f40076;
  border: 1px solid white;
  cursor: default;
}

.map-selected-existing-user-avatar {
  border: 4px solid #f40076;
}

.map-show-all-label {
  background-color: #8a51a2;
  border: 1px solid white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 100px;
}
.map-show-all-label:hover {
  background-color: #8a51a2;
  border: 1px solid white;
  cursor: default;
}
.map-show-all-label-nonperson {
  background-color: #55a541;
  border: 1px solid white;
  font-size: 12px;
  font-weight: 600;
  border-radius: 100px;
}
.map-show-all-label-nonperson:hover {
  background-color: #55a541;
  border: 1px solid white;
  cursor: default;
}

.user-avatar {
  border-radius: 100%;
  height: 72px;
  width: 72px;
  color: white;
  border: 4px solid #8a51a2;
  margin-right: auto;
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  top: 2px;
}

.avatar-img {
  border-radius: 100%;
  height: 72;
}

.location-label {
  position: relative;
  top: 2px;
}

.add-pin-icon {
  position: relative;
  top: 5px;
}

.map-selected-existing-user-avatar {
  border: 4px solid #f40076;
}

.add-location-window {
  position: absolute;
}
.add-location-field {
  margin: 0.5rem;
}
.close-button {
  margin-left: 0.5rem;
}
/deep/ .unset-width * {
  width: 200px;
}
#location-name-hint {
  font-size: 14px;
  font-weight: 400;
  color: gray;
}
</style>
